import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [adminUser,  setAdminUser]  = useState(null);
  const [voterUser,  setVoterUser]  = useState(null);
  const [loading,    setLoading]    = useState(true);

  // ── Init: Hydrate dari localStorage ───────────────────────
  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    const voterToken = localStorage.getItem('voter_token');
    const adminData  = localStorage.getItem('admin_user');
    const voterData  = localStorage.getItem('voter_user');

    if (adminToken && adminData) {
      try {
        setAdminUser(JSON.parse(adminData));
        api.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      } catch { localStorage.removeItem('admin_user'); }
    }
    if (voterToken && voterData) {
      try {
        setVoterUser(JSON.parse(voterData));
      } catch { localStorage.removeItem('voter_user'); }
    }
    setLoading(false);
  }, []);

  // ── Admin Login ───────────────────────────────────────────
  const adminLogin = useCallback(async (email, password) => {
    const res = await api.post('/auth/admin/login', { email, password });
    const { token, user } = res.data.data;

    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_user', JSON.stringify(user));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setAdminUser(user);
    return user;
  }, []);

  // ── Admin Logout ──────────────────────────────────────────
  const adminLogout = useCallback(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    delete api.defaults.headers.common['Authorization'];
    setAdminUser(null);
  }, []);

  // ── Voter Login ───────────────────────────────────────────
  const voterLogin = useCallback(async (voter_id, voter_key, election_id) => {
    const res = await api.post('/auth/voter/login', { voter_id, voter_key, election_id });
    const { token, voter, election } = res.data.data;

    localStorage.setItem('voter_token', token);
    localStorage.setItem('voter_user', JSON.stringify({ ...voter, election }));
    // voter token dikirim manual per-request di api.js
    setVoterUser({ ...voter, election });
    return { voter, election };
  }, []);

  // ── Voter Logout ──────────────────────────────────────────
  const voterLogout = useCallback(() => {
    localStorage.removeItem('voter_token');
    localStorage.removeItem('voter_user');
    setVoterUser(null);
  }, []);

  // ── Refresh admin profile ────────────────────────────────
  const refreshAdmin = useCallback(async () => {
    try {
      const res = await api.get('/auth/admin/me');
      const user = res.data.data;
      localStorage.setItem('admin_user', JSON.stringify(user));
      setAdminUser(user);
    } catch { adminLogout(); }
  }, [adminLogout]);

  const value = {
    adminUser,
    voterUser,
    loading,
    isAdminLoggedIn: !!adminUser,
    isVoterLoggedIn: !!voterUser,
    adminLogin,
    adminLogout,
    voterLogin,
    voterLogout,
    refreshAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth harus digunakan di dalam AuthProvider');
  return ctx;
};
